import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import { BadgeCard } from "@/components/BadgeCard";
import { Trophy, Zap } from "lucide-react";

function fmtUsd(cents) {
  const dollars = Math.round((cents || 0) / 100);
  return `$${dollars.toLocaleString()}`;
}

/**
 * Full-screen COD-style takeover that fires whenever a close event lands.
 * Plays the closer's walk-up track + shows a big centered banner.
 *
 * Controlled via a global `window.__mscTakeover` queue that hooks push events to.
 */
export function ArenaTakeover() {
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const el = document.createElement("audio");
    el.preload = "auto";
    audioRef.current = el;

    const enqueue = (ev) => {
      queueRef.current.push(ev);
      tick();
    };

    const tick = async () => {
      if (playingRef.current) return;
      const next = queueRef.current.shift();
      if (!next) return;
      playingRef.current = true;
      setCurrent(next);

      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => {
        setCurrent(null);
        playingRef.current = false;
        // Stop audio if still playing
        try {
          audioRef.current.pause();
          audioRef.current.src = "";
        } catch (e) {
          // ignore
        }
        // Advance queue
        setTimeout(tick, 400);
      }, 8000);
      next._timer = timer;

      // Play walk-up if not DND
      if (!next.dnd && next.user_id && next.has_walkup) {
        try {
          const resp = await fetch(`${API_BASE}/arena/walkup-track/${next.user_id}`, {
            credentials: "include",
            headers: (() => {
              const t = localStorage.getItem("msc_access_token");
              return t ? { Authorization: `Bearer ${t}` } : {};
            })(),
          });
          if (resp.ok) {
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            audioRef.current.src = url;
            audioRef.current.volume = 0.85;
            try {
              await audioRef.current.play();
            } catch (e) {
              // Autoplay blocked — silent fail (AudioArmBanner asks user to unlock)
            }
            setTimeout(() => URL.revokeObjectURL(url), 15000);
          }
        } catch (e) {
          // ignore fetch errors — takeover still shows visually
        }
      }
    };

    window.__mscTakeover = { push: enqueue };
    return () => {
      delete window.__mscTakeover;
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  if (!current) return null;

  const badges = current.badges_unlocked || [];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none"
      data-testid="arena-takeover"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

      <div
        className="relative pointer-events-auto"
        style={{
          width: "min(720px, 92vw)",
          animation: "takeoverSlam 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Angular banner */}
        <div
          className="relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(255,106,0,0.9) 0%, rgba(220,20,40,0.85) 50%, rgba(14,19,21,0.95) 100%)",
            border: "2px solid #FF6A00",
            boxShadow: "0 0 60px -10px rgba(255, 106, 0, 0.9), inset 0 0 40px rgba(0,0,0,0.5)",
            clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)",
          }}
        >
          {/* Diagonal stripes overlay */}
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage: "repeating-linear-gradient(45deg, transparent 0px, transparent 20px, rgba(255,255,255,0.05) 20px, rgba(255,255,255,0.05) 22px)",
            }}
          />

          <div className="relative px-8 py-6 flex items-start gap-4">
            <div
              className="shrink-0 flex items-center justify-center w-14 h-14 rounded-full"
              style={{
                background: "radial-gradient(circle, #FFD873 0%, #FF6A00 100%)",
                boxShadow: "0 0 30px rgba(255, 195, 60, 0.8)",
              }}
            >
              <Trophy size={28} className="text-bg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/70">
                Deal closed
              </div>
              <div
                className="text-3xl font-bold tracking-tight text-white mt-1 truncate"
                data-testid="takeover-rep-name"
                style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}
              >
                {current.user_name || "Unattributed"}
              </div>
              <div className="text-sm text-white/85 mt-1 truncate">
                {current.lead_name}
                {current.lead_city ? ` · ${current.lead_city}` : ""}
              </div>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                <div className="text-2xl font-bold tabular-nums text-white" data-testid="takeover-setup">
                  {fmtUsd(current.one_time_cents)}
                  <span className="text-xs font-normal text-white/70 ml-1">setup</span>
                </div>
                {current.mrr_cents > 0 && (
                  <div className="text-2xl font-bold tabular-nums text-white" data-testid="takeover-mrr">
                    {fmtUsd(current.mrr_cents)}
                    <span className="text-xs font-normal text-white/70 ml-1">/mo</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Badges unlocked */}
          {badges.length > 0 && (
            <div className="relative px-8 pb-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-2 flex items-center gap-1.5">
                <Zap size={12} /> Badges unlocked
              </div>
              <div className="grid grid-cols-2 gap-2">
                {badges.slice(0, 4).map((b) => (
                  <BadgeCard key={b.key} badge={{ ...b, unlocked: true }} size="sm" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA / close hint */}
        <div className="mt-3 text-center text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Auto-dismiss in a few seconds…
        </div>
      </div>

      <style>{`
        @keyframes takeoverSlam {
          0% { transform: scale(1.15) translateY(-30px); opacity: 0; }
          60% { transform: scale(0.98) translateY(4px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
